import { ProfileExtrusionType } from "building-types/profiles/profileTypesEnum";
import type { GeneratedRoof } from "building-types/roofs/generatedRoofSystemTypes";
import type { GenerateRoofOptions } from "building-types/roofs/roofGenerationTypes";
import { RoofSide } from "building-types/settings/roofTypes";
import { WallRoofBaseBoardFilter, WallRoofBaseBoardMode } from "building-types/settings/settingsTypesEnum";
import { defaultRoofFramingSettings } from "doc/settings/defaultSettings";
import { docBuildingEntityBaseNew } from "doc/utils/buildingEntityTools";
import { polyLine2ByTwoPoints } from "geom/polyLine2";
import { roofWallFacesNew, gableSubRoofWallFacesNew } from "../../roofs";

const emptyRail2 = polyLine2ByTwoPoints(-1, 0, 1, 0);
const emptyRoofWallFaces = roofWallFacesNew([], [], null, emptyRail2, emptyRail2, RoofSide.INVALID, false, 0, false);

/**
 * Returns an empty multi-gable roof.
 */
export function generatedMultiGableRoofNew({ roofId, roofData, name, roofVolume }: GenerateRoofOptions): GeneratedRoof {
  const { parentId, roofVersion, componentInstanceChain, componentTransform } = roofData;
  const {
    roofType,
    isDormer,
    isFloatingVirtualRoof,
    isVirtualRoof,
    legacyRoof,
    index,
    polygonId,
    levelId,
    roofSettings,
    roofPlanSettings,
    roofElevationPlanSettings,
    rails,
    rails2D,
  } = roofVolume;

  const instanceId = componentInstanceChain?.[0];

  return {
    ...docBuildingEntityBaseNew("ROOF", roofId, {
      type: "ROOF",
      id: roofId,
    }),
    ...(instanceId
      ? {
          pickHandle: { type: "COMPONENT_INSTANCE", id: instanceId },
        }
      : {}),
    id: roofId,
    roofSystemId: parentId,
    componentTransform,
    name,
    index,
    polygonId,
    levelId,
    roofVersion,
    isDormer,
    isVirtualRoof,
    isFloatingVirtualRoof,
    legacyRoof,
    roofType,
    roofVolume,
    wallFaces: gableSubRoofWallFacesNew(emptyRoofWallFaces, emptyRoofWallFaces),
    wallEdges: {},
    startWindows: {},
    endWindows: {},
    leftEaveWindows: {},
    rightEaveWindows: {},
    gableEndLabels: [],
    props: [],
    roofSettings,
    roofPlanSettings,
    roofElevationPlanSettings,
    roofFramingSettings: defaultRoofFramingSettings,
    wallRoofBaseBoard: WallRoofBaseBoardMode.NONE,
    wallRoofBaseBoardFilter: WallRoofBaseBoardFilter.ALL_EDGES,
    wallRoofBaseBoardProfile: ProfileExtrusionType.DEFAULT_PROFILE,
    overhangClippingEdges: [],
    suppressWallExtension: false,
    roofPerimeterRails: [],
    srcRoofPerimeterRails: [],
    friezeBoards: [],
    fasciaBoards: [],
    fasciaTopTrims: [],
    soffits: [],
    soffitRails: {},
    bellyBandBoards: [],
    wallBaseBoards: [],
    exposedRafters: [],
    gableVentFaces: [],
    trimsRoofMat: [],
    extrudedTrimsRoofMat: [],
    roofTopFaces: [],
    bottomFaces: [],
    ridgeVentFaces: [],
    ridgeCapFaces: [],
    roofTopPlates: [],
    roofingMaterialFaces: [],
    rails,
    rails2D,
    gableVents: {},
    friezeBoardRails: [],
    fasciaRails: [],
    fasciaTopTrimRails: [],
    bellyBandBoardRails: [],
    wallBaseBoardRails: [],
    componentInstanceChain,
  };
}
